import React, { useEffect } from 'react';

import { styled } from '@compiled/react';
import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { useLoggedInUser } from '@wac-web/utils/logged-in-user-provider';
import { FormattedMessage, useIntl } from 'react-intl';

import Heading from '@atlaskit/heading';
import { Stack, Inline } from '@atlaskit/primitives';

import { GenericErrorDisplay } from '@/common/displays';
import {
	ControlsPayload,
	DEFAULT_CONTROLS_PAYLOAD,
	FastshiftTable,
	OnSetControlsPayload,
} from '@/common/fastshift-table';
import { LegalBanner } from '@/common/legal-footer/src/legal-banner';
import { getCurrentTime } from '@/common/utils/get-current-time';
import { PartnerWelcomeBanner } from '@/common/welcome-banner';
import {
	getCustomerDomains,
	setTableControls,
	useFastshiftDispatch,
	useFastshiftSelector,
	TABLE_IDS,
} from '@/state';

import { useCustomerSelectionPageAnalytics } from './hooks';
import { messages } from './messages';
import { useDomainSelectionTable } from './use-domain-selection-table';

const PageContainer = styled.div({
	width: '100%',
	maxWidth: '1120px',
});

export const FastshiftCustomerSelectionPage: React.FC = () => {
	const userInfo = useLoggedInUser();
	const currentTime = getCurrentTime();

	const dispatch = useFastshiftDispatch();
	const stargateClient = useBxpWebStargateClient();
	const intl = useIntl();

	const { data: customerDomains } = useFastshiftSelector((state) => state.customerDomains);
	const { data: user } = useFastshiftSelector((state) => state.userAccess);
	const canFetchCustomerDomains = user?.hasMoveTicketAccess === true;

	const selectionTableControls = useFastshiftSelector(
		(state) => state.pageStateCache[TABLE_IDS.SELECTION],
	);

	const fetchCustomerDomains = (controls: ControlsPayload) => {
		if (!canFetchCustomerDomains) {
			return;
		}

		dispatch(
			getCustomerDomains({
				stargateClient,
				controls,
			}),
		);
	};

	useEffect(() => {
		if (!customerDomains || customerDomains.data.length === 0) {
			fetchCustomerDomains(selectionTableControls);
		}
		// we only want this effect to run once on page load once stargate client is available
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canFetchCustomerDomains, dispatch, stargateClient]);

	const onSetControlsPayload: OnSetControlsPayload = (controls: ControlsPayload) => {
		dispatch(setTableControls({ tableId: TABLE_IDS.SELECTION, controls }));
		fetchCustomerDomains(controls);
	};

	useCustomerSelectionPageAnalytics();

	const { isTableLoading, isTableError, rows, head, pagination } = useDomainSelectionTable();

	const ErrorComponent = (
		<GenericErrorDisplay
			customMessage={intl.formatMessage(messages.errorMessage)}
			onRefresh={() => {
				fetchCustomerDomains(DEFAULT_CONTROLS_PAYLOAD);
			}}
		/>
	);

	return (
		<Inline alignInline="center">
			<PageContainer>
				<Stack space="space.400">
					<Stack space="space.300">
						<PartnerWelcomeBanner currentTime={currentTime} userName={userInfo?.name} />
						<LegalBanner />
					</Stack>

					<Stack space="space.200">
						<Heading size="small" as="h2">
							<FormattedMessage {...messages.customerSelectionTableHeading} />
						</Heading>

						<FastshiftTable
							id="customer-selection-table"
							enableSearch={true}
							searchAriaLabel={intl.formatMessage(messages.searchAriaLabel)}
							tableCaption={intl.formatMessage(messages.tableCaption)}
							summaryText={intl.formatMessage(messages.summaryText, {
								viewSize: rows.length,
								totalNumber: pagination.totalElements,
							})}
							isTableLoading={isTableLoading}
							isTableError={isTableError}
							errorComponent={ErrorComponent}
							head={head}
							rows={rows}
							controls={{
								onSetControlsPayload,
								controls: selectionTableControls,
								pagination,
							}}
						/>
					</Stack>
				</Stack>
			</PageContainer>
		</Inline>
	);
};
